import { createContext, createElement, useContext } from 'react';

export type ReactLinkHandler = {
  Link: LinkComponent;
};

export const ReactLinkContext = createContext<ReactLinkHandler>({
  Link: ({ children, to, href, replace: _replace, ...props }) => {
    if (!to || typeof to === 'string') {
      return createElement('a', { href: to || href, ...props }, children);
    }

    const { pathname, search, hash } = to;
    const path = [pathname, search].filter(Boolean).join('?');
    const linkHref = [path, hash].filter(Boolean).join('#');

    return createElement('a', { href: linkHref, ...props }, children);
  },
});

export function useReactLinkHandler(): ReactLinkHandler {
  return useContext(ReactLinkContext);
}

export function useLink(): LinkComponent {
  return useReactLinkHandler().Link;
}

export type BaseAnchorProps = Omit<
  React.ComponentPropsWithoutRef<'a'>,
  'href' | 'onClick' | 'onMouseOver' | 'onFocus'
>;

export type To =
  | string
  | { pathname: string; hash?: string; search?: string; state?: unknown }
  | undefined;

export type LinkProps = React.PropsWithChildren<
  BaseAnchorProps & {
    to?: To;
    href?: string;
    replace?: boolean;
    state?: unknown;
    onClick?: React.MouseEventHandler<HTMLAnchorElement>;
    onMouseOver?: React.MouseEventHandler<HTMLAnchorElement>;
    onFocus?: React.FocusEventHandler<HTMLAnchorElement>;
  }
>;

export type LinkComponent = React.ComponentType<LinkProps>;
